import React, { useState, useEffect, useRef, useMemo, useCallback, type ReactNode, type RefObject } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IconEdit } from '@bynder/icons';
import * as types from 'packages/store/types';
import { isCtrlKey } from 'packages/helpers/hotKeys';

type Props = {
    withDoubleClick?: boolean;
    hideIcon: boolean;
    editable: boolean;
    className?: string;
    value: string;
    ignoreChanges?: boolean;
    onToggle: (value: boolean) => void;
    scrollContainerRef?: RefObject<HTMLElement | null>;
    valueNodeRef?: RefObject<HTMLElement | null>;
    wrapperClassName?: string;
    validations?: { maxLength: number };
    alwaysShowButton?: boolean;
    confirmBeforeUnmount?: boolean;
    displayValue?: string | ReactNode;
    editOnlyWithIcon?: boolean;
    onConfirm: (value: string) => void;
    handleChange?: (e: InputEvent) => void;
    useCapture?: boolean;
    handleValidationFail?: (value: string) => void;
    allowOpen?: boolean;
    emptyValueStyles?: any;
    confirmEmpty?: boolean;
    onMountCb?: () => void;
};

function Editable({
    withDoubleClick = false,
    hideIcon,
    editable,
    className,
    value: propValue,
    ignoreChanges: ignoreChangesProp = false,
    onToggle,
    scrollContainerRef,
    valueNodeRef,
    wrapperClassName,
    validations: validationsProp,
    alwaysShowButton,
    confirmBeforeUnmount,
    displayValue: displayValueProp,
    editOnlyWithIcon = false,
    onConfirm,
    handleChange: handleChangeProp,
    useCapture,
    handleValidationFail,
    allowOpen,
    emptyValueStyles,
    confirmEmpty = false,
    onMountCb,
}: Props) {
    const isEditingBatchComponent = useSelector((state: any) => state.general.isEditingBatchComponent);
    const dispatch = useDispatch();

    const [ignoreChanges] = useState(ignoreChangesProp);
    const [invalid] = useState(false);
    const [value, setValue] = useState(propValue);
    const [width, setWidth] = useState<number | null>(null);
    const [validations] = useState<{ maxLength: number } | Record<string, never>>(validationsProp || {});

    const nodeRef = useRef<HTMLElement | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const setIsEditingBatchComponent = useCallback(
        (val: boolean) => {
            dispatch({
                type: types.BATCH_IS_EDITING_COMPONENT,
                isEditingBatchComponent: val,
            });
        },
        [dispatch],
    );

    const validate = useCallback(
        (v: string) => {
            if (validations && 'maxLength' in validations && validations.maxLength < v.length) {
                if (handleValidationFail) {
                    handleValidationFail(
                        `Name should not be longer than ${validations.maxLength} characters. Please shorten the name to proceed`,
                    );

                    return { maxLength: true };
                }

                return true;
            }

            return false;
        },
        [validations, handleValidationFail],
    );

    const closeRef = useRef<() => void>(() => {});
    const handleConfirmRef = useRef<() => void>(() => {});

    const close = useCallback(() => {
        const findBatchPlayButton = document.querySelectorAll('.batch--generation-play-btn');
        findBatchPlayButton?.forEach((btn) => btn.removeAttribute('disabled'));

        onToggle(false);
        setIsEditingBatchComponent(!isEditingBatchComponent);
        document.removeEventListener('click', handleOutsideClickRef.current, !!useCapture);
    }, [onToggle, isEditingBatchComponent, setIsEditingBatchComponent, useCapture]);

    closeRef.current = close;

    const handleConfirm = useCallback(() => {
        if (invalid) {
            return;
        }

        if (!!value && !!value.trim() && value !== propValue) {
            onConfirm(value);
        } else if (confirmEmpty && value !== propValue) {
            onConfirm('');
        } else {
            setValue(propValue);
            closeRef.current();
        }
    }, [invalid, value, propValue, onConfirm, confirmEmpty]);

    handleConfirmRef.current = handleConfirm;

    const handleOutsideClick = useCallback((e: MouseEvent) => {
        if (!nodeRef.current || nodeRef.current.contains(e.target as Node)) {
            return;
        }

        handleConfirmRef.current();
    }, []);

    const handleOutsideClickRef = useRef(handleOutsideClick);
    handleOutsideClickRef.current = handleOutsideClick;

    const open = useCallback(() => {
        timeoutRef.current = setTimeout(() => {
            const findBatchPlayButton = document.querySelector('.active--row .batch--generation-play-btn');
            findBatchPlayButton?.setAttribute('disabled', 'true');

            onToggle(true);
            setIsEditingBatchComponent(!isEditingBatchComponent);
            document.addEventListener('click', handleOutsideClickRef.current, !!useCapture);

            if (scrollContainerRef && scrollContainerRef.current) {
                scrollContainerRef.current.scrollLeft = 0;
            }
        }, 200);
    }, [onToggle, isEditingBatchComponent, setIsEditingBatchComponent, useCapture, scrollContainerRef]);

    const valueLayerProps = useMemo(() => {
        const props: { onClick?: () => void; onDoubleClick?: () => void } = {};

        if (!editOnlyWithIcon) {
            if (withDoubleClick) {
                props.onDoubleClick = () => open();
            } else {
                props.onClick = () => open();
            }
        }

        return props;
    }, [editOnlyWithIcon, withDoubleClick, open]);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (handleChangeProp) {
                handleChangeProp(e.target as unknown as InputEvent);
            } else {
                let newValue = e.target.value;
                const error = validate(newValue);

                if (error) {
                    if (typeof error === 'object' && Object.keys(error).includes('maxLength')) {
                        newValue = newValue.substr(0, newValue.length - 1);
                    }
                }

                setValue(newValue);
                setWidth(e.target.scrollWidth);
            }
        },
        [handleChangeProp, validate],
    );

    const moveCaretAtEnd = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            e.target.value = value;
            setWidth(e.target.scrollWidth);
            e.target.select();
        },
        [value],
    );

    const handleKeyUp = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && !isCtrlKey(e)) {
            handleConfirmRef.current();
        }
    }, []);

    // componentDidMount
    useEffect(() => {
        onMountCb?.();
    }, []);

    // Sync value from props
    useEffect(() => {
        if (!ignoreChanges) {
            setValue(propValue);
        }
    }, [propValue, ignoreChanges]);

    // Manage editable state and event listeners
    useEffect(() => {
        if (!editable) {
            document.removeEventListener('click', handleOutsideClickRef.current, !!useCapture);
        } else if (allowOpen) {
            open();
        }
    }, [editable]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            closeRef.current();

            if (confirmBeforeUnmount) {
                handleConfirmRef.current();
            }

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [confirmBeforeUnmount]);

    const displayedValue = handleChangeProp ? propValue : value;
    const displayValue = displayValueProp || displayedValue;

    const EditableElem = (
        <div className="input-group">
            <input
                type="text"
                onScroll={(e) => e.stopPropagation()}
                maxLength={
                    'maxLength' in validations ? (validations as { maxLength: number }).maxLength + 1 : undefined
                }
                onChange={handleInputChange}
                style={{ width: width?.toString() }}
                value={displayedValue}
                className={`form-control-plaintext mw-100 p-0 ${className || ''}`}
                onFocus={moveCaretAtEnd}
                onBlur={() => handleConfirmRef.current()}
                ref={(ref) => {
                    ref?.focus({ preventScroll: true });
                }}
            />
        </div>
    );

    return (
        <div className={`${wrapperClassName || ''}`}>
            {editable && (
                <div
                    role="presentation"
                    onKeyUp={handleKeyUp}
                    ref={(node) => {
                        nodeRef.current = node;
                    }}
                >
                    {EditableElem}
                </div>
            )}
            {!editable && (
                <p className={`m-0 cursor-pointer ${className || ''}`}>
                    <span {...valueLayerProps} ref={valueNodeRef} style={displayValue ? {} : emptyValueStyles}>
                        {displayValue}
                    </span>
                    {!hideIcon && (
                        <button
                            type="button"
                            onClick={() => open()}
                            className={`btn btn-secondary btn-sm ${alwaysShowButton ? '' : 'text-hover-show'} ml-2 p-0`}
                        >
                            <IconEdit />
                        </button>
                    )}
                </p>
            )}
        </div>
    );
}

export default Editable;
